import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://192.20.192.87:7501/api";

const PaymentCallback = () => {
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();
  const verifiedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference =
      params.get("reference") ||
      params.get("trxref") ||
      sessionStorage.getItem("paystack_reference");

    if (!reference) {
      setStatus("failed");
      setMessage("No payment reference found.");
      return;
    }

    const verifyPayment = async (retry = 0) => {
      if (verifiedRef.current) return;
      
      try {
        const response = await axios.get(`${API_BASE_URL}/payment/verify/${reference}`, {
          timeout: 15000
        });
        
        if (response.data.status === "success") {
          verifiedRef.current = true;
          sessionStorage.removeItem("paystack_reference");
          setStatus("success");
          setMessage("Your VPS subscription is now active. A confirmation email will be sent to you shortly.");
          setTimeout(() => navigate("/"), 5000);
        } else if (response.data.status === "pending") {
          // Payment still processing - wait and retry
          if (retry < 10) {
            setRetryCount(retry + 1);
            setTimeout(() => verifyPayment(retry + 1), 3000);
          } else {
            setStatus("pending");
            setMessage("Your payment is being processed. You will receive a confirmation email shortly.");
          }
        } else {
          setStatus("failed");
          setMessage(response.data.message || "Payment was not completed. Please try again.");
        }
      } catch (err) {
        console.error("Verification error:", err);
        
        // Retry up to 3 times on network errors
        if (retry < 3) {
          setRetryCount(retry + 1);
          setTimeout(() => verifyPayment(retry + 1), 2000);
        } else {
          setStatus("failed");
          setMessage("We could not verify your payment. Please check your email for confirmation or contact support.");
        }
      }
    };

    verifyPayment();
  }, [navigate]);

  // Show pending status
  if (status === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#001F3F] text-white px-4">
        <div className="text-center p-10 bg-[#022A52] border border-[#033A6A] rounded-3xl max-w-md w-full shadow-2xl">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-yellow-400 border-t-transparent mx-auto mb-6" />
          <h2 className="text-xl font-bold text-yellow-400">Payment Processing</h2>
          <p className="text-gray-300 text-sm mt-2">{message || "Your payment is being processed. You will receive a confirmation email shortly."}</p>
          <button
            onClick={() => navigate("/")}
            className="mt-8 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#001F3F] text-white px-4">
      <div className="text-center p-10 bg-[#022A52] border border-[#033A6A] rounded-3xl max-w-md w-full shadow-2xl">

        {status === "verifying" && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-400 border-t-transparent mx-auto mb-6" />
            <h2 className="text-xl font-bold text-white">Verifying your payment...</h2>
            {retryCount > 0 && (
              <p className="text-gray-400 text-sm mt-2">Retry attempt {retryCount} of 3...</p>
            )}
            <p className="text-gray-400 text-sm mt-2">Please wait, do not close this page.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-500 bg-opacity-20 mx-auto mb-6">
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-green-400 mb-3">Payment Successful!</h2>
            <p className="text-gray-300 text-sm leading-relaxed">{message}</p>
            <p className="text-gray-500 text-xs mt-6">Redirecting you to the homepage in 5 seconds...</p>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-500 bg-opacity-20 mx-auto mb-6">
              <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-red-400 mb-3">Payment Failed</h2>
            <p className="text-gray-300 text-sm leading-relaxed">{message}</p>
            <button
              onClick={() => navigate("/")}
              className="mt-8 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition"
            >
              Back to Home
            </button>
          </>
        )}

      </div>
    </div>
  );
};

export default PaymentCallback;